import CustomForm from "../../CustomForm";
import m, {Vnode} from "mithril";
import ICustomFormSettings from "../../ICustomFormSettings";
import {TextareaItem} from "../../../model/items/textarea";
import {TextboxItem} from "../../../model/items/textbox";

export default class LocationGet extends CustomForm {

    private readonly latInput!: TextboxItem;
    private readonly lngInput!: TextboxItem;
    private locationInfo?: TextareaItem;

    private locationInProgress = false;
    private showPrompt = false;

    constructor(settings: ICustomFormSettings) {
        super(settings);

        for (const cell of this.settings.form.grid.cells) {
            for (let cellItem of cell.cellItems) {
                if (cellItem instanceof TextboxItem) {
                    if (this.latInput) {
                        this.lngInput = cellItem;
                    } else {
                        this.latInput = cellItem;
                    }
                } else if (cellItem instanceof TextareaItem) {
                    this.locationInfo = cellItem;
                }
            }
        }

        // Skip question, if device not capable to get location
        if (!this.hasLocationService()) {
            this.safetyNextForm();
        }
    }

    checkRequirements(): boolean {
        if (!this.latInput || !this.lngInput) {
            console.log('customForm-Location-get: expect 2 Textbox fields for latitude and longitude.');
            return false;
        }
        return true;
    }

    private hasLocationService(): boolean {
        return Boolean(navigator.geolocation);
    }

    private tryToGetLocationAndSkipForm(): void {

        // Skip if location is already set
        if (this.lngInput.getVariable() && this.latInput.getVariable()) {
            this.safetyNextForm();
        }

        // Read permission without prompt user
        navigator.permissions && navigator.permissions.query({name: 'geolocation'})
            .then((permissionStatus) => {
                if (permissionStatus.state == 'granted') {
                    this.getLocation();
                } else if (permissionStatus.state == 'prompt') {
                    // Show form with explanation why we need location
                    if (!this.showPrompt) {
                        this.showPrompt = true;
                    } else {
                        this.getLocation();
                        this.showPrompt = false;
                    }
                    m.redraw();
                } else {
                    this.locationInfo?.set( "Error: Not allowed");
                    this.safetyNextForm();
                }
            }).catch(reason => {
                this.settings.form.questionary.eventReporter?.report("GPS_FAILED", reason);
                this.safetyNextForm();
            })
    }

    private getLocation() {
        this.locationInProgress = true;
        m.redraw();
        navigator.geolocation.getCurrentPosition((pos) => {
            this.latInput.set(pos.coords.latitude);
            this.lngInput.set(pos.coords.longitude);
            this.locationInfo?.set(this.positionToJson(pos));
            this.safetyNextForm();
        }, (error) => {
            this.locationInfo?.set( "Error: "+error.code+" "+error.message);
            this.locationInProgress = false;
            this.safetyNextForm();
            m.redraw();
        });
    }

    private viewSendLocationConsent(): Vnode {
        const questionary = this.settings.form.questionary;
        return m('.text-center', {}, [
            m('button.btn.raised.primary', {
                onclick: (e: MouseEvent) => {
                    e.preventDefault();
                    this.tryToGetLocationAndSkipForm();
                },
            }, questionary.renderText(questionary.messages.get('allowLocation'))),
            m.trust('&nbsp;&nbsp;'),
            m('button.btn.secondary.outline', {
                onclick: (e: MouseEvent) => {
                    e.preventDefault();
                    this.safetyNextForm();
                },
            }, questionary.renderText(questionary.messages.get('skip')))
        ]);
    }

     public view(): Vnode {

        const questionary = this.settings.form.questionary;
        const status = this.locationInProgress ? questionary.messages.get( 'acquiringLocation') : ' ';

        return this.viewLayout(
            m('form', {},
                m('i.icon.icon-map-maker', {}, ),
                m('p', {},
                    questionary.renderText(status),
                ),
                this.showPrompt ? this.viewSendLocationConsent() : null,
            )
        );
    }

    private safetyNextForm() {
        if (this.settings.form === this.settings.form.questionary.currentForm) {
            this.settings.form.questionary.next();
            m.redraw();
        }
    }

    public oninit() {
        this.tryToGetLocationAndSkipForm();

        this.controls.onNext = () => {
            if (this.locationInProgress) {
                this.safetyNextForm();
            } else {
                this.locationInProgress = true;
                this.getLocation();
            }
        };
    }

    private positionToJson(pos: GeolocationPosition): string {
        return JSON.stringify({
            coords: {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                speed: pos.coords.speed,
                accuracy: pos.coords.accuracy,
                altitude: pos.coords.altitude,
            },
            timestamp: pos.timestamp
        });
    }
}